import { Application } from '@/types/application';
import { BackendApplicationRevision, Release } from '@/types/backend';
import { ClipboardData, ClipboardNode, ClipboardNodeData, ClipboardSubroutineData } from '@/types/clipboard';
import { Connection } from '@/types/connection';
import { emptyValue } from '@/types/detail';
import { Folder } from '@/types/folder';
import {
  Configuration,
  ConfigurationEnum,
  ConfigurationMultiple,
  EventHandler,
  ListType,
  Metadata
} from '@/types/metadata/metadata';
import { Prompt } from '@/types/prompt';
import { KeyValue, VariableRef } from '@/types/variable';
import { XYPosition } from '@xyflow/react';

export const isArray = (obj: unknown, itemType?: string | string[]): obj is any[] =>
  Array.isArray(obj) &&
  (itemType
    ? obj.every((item: any) => (Array.isArray(itemType) ? itemType.includes(typeof item) : typeof item === itemType))
    : true);

export const isString = (obj: unknown): obj is string => typeof obj === 'string';

export const isNumber = (obj: unknown): obj is number => typeof obj === 'number' && !isNaN(obj);

export const isObject = (obj: unknown): obj is Record<string, unknown> =>
  typeof obj === 'object' && obj !== null && !isArray(obj);

/**
 * Deeply compares two objects for equality.
 *
 * @param {any} a - The first object to compare.
 * @param {any} b - The second object to compare.
 * @param {string[]} [keysToCheck] - An optional array of keys to check for deep equality. If not provided, all keys will be checked.
 * @returns {boolean} Returns true if the objects are deeply equal according to the keys specified, otherwise returns false.
 */
export const deepEqual = (a: any, b: any, keysToCheck?: string[]): boolean => {
  if (a === b) return true;

  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
    return false;
  }

  const keysA = keysToCheck ?? Object.keys(a);

  for (const key of keysA) {
    if (!(key in a) || !(key in b)) return false; // Check if key exists in both objects
    if (!deepEqual(a[key], b[key])) {
      return false;
    }
  }

  return true;
};

export const isMetadata = (obj: unknown): obj is Metadata => {
  return typeof obj === 'object' && obj !== null && 'blocks' in obj && isArray(obj.blocks);
};

export const isApplication = (arg: any): arg is Application => {
  return (
    arg &&
    typeof arg.name === 'string' &&
    typeof arg.label === 'string' &&
    typeof arg.generated === 'string' &&
    typeof arg.format === 'number' &&
    typeof arg.subroutines === 'object' &&
    typeof arg.blocks === 'object' &&
    typeof arg.variables === 'object' &&
    typeof arg.prompts === 'object'
  );
};

export const isPrompt = (obj: unknown): obj is Prompt =>
  typeof obj === 'object' &&
  obj !== null &&
  'id' in obj &&
  'file' in obj &&
  'label' in obj &&
  'path' in obj &&
  typeof obj.id === 'number' &&
  typeof obj.label === 'string' &&
  (typeof obj.path === 'object' || typeof obj.path === 'string') &&
  typeof obj.file === 'string';

export const isVariable = (obj: unknown): obj is VariableRef =>
  typeof obj === 'object' &&
  obj !== null &&
  'name' in obj &&
  'scope' in obj &&
  typeof obj.name === 'string' &&
  typeof obj.scope === 'string';

export const isKeyValue = (obj: unknown): obj is KeyValue<string> =>
  typeof obj === 'object' && obj !== null && 'key' in obj && 'value' in obj && typeof obj.key === 'string';

export const isConnection = (obj: unknown): obj is Connection =>
  isObject(obj) && 'action' in obj && 'label' in obj && 'dst' in obj;

export const isXYPosition = (obj: any): obj is XYPosition => {
  return obj && typeof obj.x !== 'undefined' && typeof obj.y !== 'undefined';
};

export const isFolder = (obj: unknown): obj is Folder => {
  return typeof obj === 'object' && obj !== null && 'folderId' in obj;
};

export const isListType = (obj: unknown): obj is ListType => {
  return typeof obj === 'object' && obj !== null && 'types' in obj;
};

export const isError = (obj: unknown): obj is Error => {
  return typeof obj === 'object' && obj !== null && 'message' in obj && 'stack' in obj;
};

export const isBackendApplicationRevision = (obj: unknown): obj is BackendApplicationRevision => {
  return typeof obj === 'object' && obj !== null && 'applicationId' in obj && 'revision' in obj;
};

export const isRelease = (obj: unknown): obj is Release => {
  return typeof obj === 'object' && obj !== null && 'releaseId' in obj && 'revision' in obj;
};

export const isBackendApplicationRevisionArray = (obj: unknown): obj is BackendApplicationRevision[] => {
  return isArray(obj) && obj.length > 0 && obj.every((element) => isBackendApplicationRevision(element));
};

export const isReleaseArray = (obj: unknown): obj is Release[] => {
  return isArray(obj) && obj.length > 0 && obj.every((element) => isRelease(element));
};

export const isEventHandler = (obj: unknown): obj is EventHandler =>
  isObject(obj) && ('eventstart' in obj || 'arguments' in obj);

export const isClipboardNode = (obj: unknown): obj is ClipboardNode => isObject(obj) && 'ID' in obj && 'node' in obj;

export const isClipboardData = (obj: unknown): obj is ClipboardData =>
  isClipboardNodeData(obj) || isClipboardSubroutineData(obj);

export const isClipboardNodeData = (obj: unknown): obj is ClipboardNodeData =>
  isObject(obj) &&
  'type' in obj &&
  'nodes' in obj &&
  obj.type === 'nodes' &&
  isArray(obj.nodes) &&
  !!obj.nodes.length &&
  obj.nodes.every((node) => isClipboardNode(node));

export const isClipboardSubroutineData = (obj: unknown): obj is ClipboardSubroutineData =>
  isObject(obj) &&
  'type' in obj &&
  obj.type === 'subroutines' &&
  'data' in obj &&
  isArray(obj.data) &&
  !!obj.data.length &&
  obj.data.every(
    (data) =>
      'subroutine' in data &&
      'nodes' in data &&
      'variables' in data &&
      isArray(data.nodes) &&
      data.nodes.every((node: unknown) => isClipboardNode(node))
  );

export const isConfiguration = (obj: unknown): obj is Configuration =>
  isObject(obj) && 'value' in obj && 'required' in obj;

export const isConfigurationEnum = (obj: unknown): obj is ConfigurationEnum =>
  isConfiguration(obj) && 'sections' in obj && 'options' in obj;

export const isConfigurationMultiple = (obj: unknown): obj is ConfigurationMultiple =>
  isConfiguration(obj) && 'sections' in obj && !('options' in obj);

export const isEmptyValue = (value: unknown): value is emptyValue => Boolean(isObject(value) && value.empty);

export const isNullOrUndefined = (obj: unknown): obj is null | undefined => obj === null || obj === undefined;
